import { formatPersonName } from '@/utils/personName';
/**
 * FiduciaryPersonDialog — Fiduciary-group entry point for the RAO people form.
 *
 * Composes the Fiduciary section suite on top of the shared modal shell.
 * CIP scan section renders FIRST (per §5 CIP-reposition, consistent with
 * Individual) — a fiduciary IS a human with a photo ID, so CIP capture
 * applies. Followed by Identity (sub-type picker + ward linkage +
 * appointment block), Address, Contact, Document slots.
 *
 * Architecture notes:
 *   - Mirrors `IndividualPersonDialog.tsx` structure — always-modal surface,
 *     same attachedDocs lifting pattern, same change-tracker helper.
 *   - `useFiduciaryDerivation` surfaces customerType + supervisor-approval
 *     gate (Conservator / VAFiduciary / authority-mismatch trip the gate).
 *   - Citizenship + Employment sections are OMITTED — per card, fiduciary is
 *     acting in a legal capacity on behalf of someone else; their personal
 *     citizenship/employment isn't part of the entity record.
 *   - CIP scan is ENABLED (`cipScanEnabled=true`) per build-card rules — the
 *     fiduciary's own photo ID is captured here.
 *   - Appointment-document (SSA letter / court order / VA packet) attaches via
 *     the shared DocumentSlotsSection, which resolves sub-type-specific
 *     docCodes from EntityRuleRequiredDocs.
 *
 * TODO(bill): fiduciary walkthrough needed — §15 deferred items:
 *   - Protected-person CIF auto-link (currently free-text in Identity section)
 *   - Public Administrator override flow (Conservator, Dana R Sooter lookup)
 *   - RepPayee auto-generated Stops/Holds message (expiration 12/31/2099)
 *   - VAFiduciary signing-authority manipulation on the cart
 *   - USDAGrant + MedicalSetAside sub-types (out of current scope)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalShell } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import { FiduciaryCipSection } from './FiduciaryCipSection';
import { FiduciaryIdentitySection } from './FiduciaryIdentitySection';
import { useFiduciaryDerivation } from './useFiduciaryDerivation';

// Shared sections from personDialog/shared/
import { AddressSection } from '../shared/AddressSection';
import { ContactSection } from '../shared/ContactSection';
import { DocumentSlotsSection } from '../shared/DocumentSlotsSection';
import { PdwFooterDyad } from '../shared/PdwFooterDyad';
import {
  type AttachedDoc,
  readAttachedDocs,
  mergeAttachedDoc,
  mergeAttachedDocsKeepExisting,
} from '../shared/attachedDocs';
import { useAttachedDocsHydration } from '../shared/useInquiryHydration';
import { EntityConvergedShell } from '../EntityConvergedShell';
import type { PdwTabKey } from '../PdwTabStrip';
import { withMiddleInitial } from '../shared/middleInitial';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export type FiduciaryPersonDialogMode = 'review-add' | 'new' | 'edit';

export interface FiduciaryPersonDialogProps {
  open: boolean;
  mode: FiduciaryPersonDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  cartId: string;
  officerCode?: string;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function FiduciaryPersonDialog({
  open,
  mode,
  person,
  onSave,
  onClose,
  cartCifIds,
  cartId,
  officerCode,
  readOnly = false,
}: FiduciaryPersonDialogProps): JSX.Element | null {
  const [local, setLocal] = useState<RaoPerson>(person);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Slot-4 T0 (Contract C2) — attachedDocs live on the REAL draft
  // (local.extra.attachedDocs), not a sibling useState that drops on save.
  const attachedDocs = useMemo<ReadonlyArray<AttachedDoc>>(() => readAttachedDocs(local), [local]);

  const registerAttachedDoc = useCallback((doc: AttachedDoc): void => {
    setLocal((prev) => mergeAttachedDoc(prev, doc));
    setHasChanges(true);
  }, []);

  useEffect(() => {
    setLocal(person);
    setHasChanges(false);
  }, [person]);

  // Slot-4 T5 — Edit-mode doc hydration from the v3 Document Library.
  const { fetchAttachedDocsForCif } = useAttachedDocsHydration();
  const hydratedCifRef = useRef<string | null>(null);
  useEffect(() => {
    const cifId = (person.cifId ?? '').trim();
    if (mode === 'new' || !cifId || hydratedCifRef.current === cifId) return;
    hydratedCifRef.current = cifId;
    let cancelled = false;
    void (async () => {
      const docs = await fetchAttachedDocsForCif(cifId);
      if (cancelled || docs.length === 0) return;
      setLocal((prev) => mergeAttachedDocsKeepExisting(prev, docs));
    })();
    return () => {
      cancelled = true;
    };
  }, [person.cifId, mode, fetchAttachedDocsForCif]);

  const sectionMode: 'new' | 'existing' = mode === 'new' ? 'new' : 'existing';
  const derivation = useFiduciaryDerivation(local, {
    mode: sectionMode,
    storedType: person.customerType,
  });

  const update = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const next: RaoPerson = { ...prev, ...withMiddleInitial(patch) };
      const fn = next.firstName ?? '';
      const mn = next.middleName ?? '';
      const ln = next.lastName ?? '';
      const display = [fn, mn, ln].filter((x) => !!x).join(' ').trim();
      if (display) next.fullName = display;
      if (prev.mode === 'existing') next.dirty = true;
      return next;
    });
    setHasChanges(true);
  }, []);

  const isChanged = useCallback(
    (field: string): boolean => {
      const a = (local as unknown as Record<string, unknown>)[field];
      const b = (person as unknown as Record<string, unknown>)[field];
      return a !== b && !(a == null && b == null);
    },
    [local, person],
  );

  const displayName =
    local.firstName || local.lastName
      ? formatPersonName(local)
      : local.name2 ?? '';

  // customerType is typed as CustomerType | 'Minor' | undefined in shared-types;
  // Fiduciary sub-types (RepPayee / Conservator / VAFiduciary) aren't in the
  // enum yet — TODO(bill): fiduciary walkthrough needed to lift these into
  // the shared-types CustomerType union. For now, widen to string for display.
  const subTypeKey = String(local.customerType ?? '');
  const subTypeLabel =
    subTypeKey === 'RepPayee'
      ? 'Rep Payee'
      : subTypeKey === 'Conservator'
        ? 'Conservator'
        : subTypeKey === 'VAFiduciary'
          ? 'VA Fiduciary'
          : 'Fiduciary';

  const title =
    mode === 'review-add'
      ? `Review & add — ${subTypeLabel}: ${displayName}`
      : mode === 'new'
        ? `Add new ${subTypeLabel}`
        : `Edit ${subTypeLabel} — ${displayName}`;

  const btnLabel =
    mode === 'review-add'
      ? hasChanges
        ? 'Add existing w/ changes'
        : 'Add existing'
      : mode === 'new'
        ? 'Add new'
        : 'Save changes';

  const dupInCart =
    !!(local.cifId && cartCifIds?.includes(local.cifId)) && mode === 'review-add';
  const saveDisabled = dupInCart || !!derivation.requiresSupervisorApproval;

  // BACKLOG-0016 — sticky-footer Return/Continue dyad (flat-scroll dialog).
  const continueDisabledReason = dupInCart
    ? `CIF ${local.cifId ?? ''} is already on this cart.`
    : derivation.requiresSupervisorApproval
      ? 'Supervisor approval required before saving.'
      : undefined;

  // ---------------------------------------------------------------------------
  // Sections grouped by canonical tab (D3 convergence). Fiduciary surfaces a
  // subset: CIP (fiduciary's own photo ID) · Profile (ward linkage) · Contact ·
  // Documents (SSA letter / court order / VA packet). Citizenship + Employment
  // omitted per build-card rules.
  // ---------------------------------------------------------------------------
  const sectionsByTab: Partial<Record<PdwTabKey, JSX.Element>> = {
    cip: (
      <FiduciaryCipSection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        onDocumentCaptured={registerAttachedDoc}
        readOnly={readOnly}
        cipScanEnabled={true}
      />
    ),
    profile: (
      <FiduciaryIdentitySection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        derivation={derivation}
        readOnly={readOnly}
      />
    ),
    contact: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AddressSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
        <ContactSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
      </div>
    ),
    documents: (
      <DocumentSlotsSection
        person={local}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        entityType={local.customerType ?? 'RepPayee'}
        attachedDocs={attachedDocs}
        onDocumentRegistered={registerAttachedDoc}
        readOnly={readOnly}
      />
    ),
  };

  // Modal surface — every entry into the dialog (New Acct + Maint CIF).
  return (
    <ModalShell open={open} onClose={onClose} title={title} size="lg">
      <EntityConvergedShell
        person={local}
        mode={mode}
        dupInCart={dupInCart}
        tabs={['review', 'cip', 'profile', 'contact', 'documents']}
        sectionsByTab={sectionsByTab}
        onAttachedDocsChange={(docs) => docs.forEach(registerAttachedDoc)}
        onPersonPatch={update}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        readOnly={readOnly}
      />

      <PdwFooterDyad
        currentStep={0}
        totalSteps={1}
        onReturn={onClose}
        onContinue={() => onSave(local)}
        continueDisabled={saveDisabled || readOnly}
        continueDisabledReason={continueDisabledReason}
        lastStepLabel={`${btnLabel} →`}
      />
    </ModalShell>
  );
}
